import { EDITIONS, type EpisodeStatus, type LibraryStats, NotFoundError, TERM_KINDS } from '@zyx/shared';
import type { ReactNode } from 'react';
import { PageHeader } from '@/components/common/page-header';
import { ErrorState } from '@/components/common/states';
import { ColumnChart } from '@/components/stats/column-chart';
import { Skeleton } from '@/components/ui/skeleton';
import { useStats } from '@/hooks/use-movies';
import { formatBytes } from '@/lib/crawl-labels';
import { editionLabel } from '@/lib/format';
import { addedInLast, dayPoints, localDate, yearPoints } from '@/lib/stats';
import { cn } from '@/lib/utils';
import { PLURALS } from './collections-page';

/** In the order they read best, the healthy ones first; a colour always sits beside its name. */
const STREAM_PARTS: ReadonlyArray<{ readonly status: EpisodeStatus; readonly label: string; readonly className: string }> = [
  { status: 'ok', label: 'Playable', className: 'bg-success' },
  { status: 'unchecked', label: 'Not checked yet', className: 'bg-pending' },
  { status: 'cdn_unresolvable', label: 'Dead host', className: 'bg-warning' },
  { status: 'error', label: 'Check failed', className: 'bg-destructive' },
  { status: 'gone', label: 'Gone', className: 'bg-destructive' },
];

const TILE_GRID = 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4';

function Tile(props: { readonly label: string; readonly value: string; readonly hint?: string }) {
  return (
    <div className="rounded-lg border border-border px-4 py-3">
      <p className="text-xs text-muted-foreground">{props.label}</p>
      <p className="text-2xl font-semibold">{props.value}</p>
      {props.hint === undefined ? null : <p className="text-xs text-muted-foreground">{props.hint}</p>}
    </div>
  );
}

function Section(props: { readonly title: string; readonly note?: string; readonly children: ReactNode }) {
  return (
    <section className="min-w-0 space-y-3">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">{props.title}</h2>
        {props.note === undefined ? null : <p className="text-xs text-muted-foreground">{props.note}</p>}
      </div>
      {props.children}
    </section>
  );
}

/** Most first, each bar with its value at the tip. */
function EditionBars(props: { readonly editions: LibraryStats['editions'] }) {
  const rows = EDITIONS.map((edition) => ({ edition, movies: props.editions[edition] }))
    .filter((row) => row.movies > 0)
    .sort((left, right) => right.movies - left.movies);
  const peak = rows[0]?.movies ?? 1;
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing indexed yet.</p>;
  }
  return (
    <ul className="space-y-3">
      {rows.map((row) => (
        <li key={row.edition} className="grid grid-cols-[6rem_1fr] items-center gap-3 text-sm">
          <span className="truncate text-muted-foreground">{editionLabel(row.edition)}</span>
          <div className="flex items-center gap-2">
            {/* The room kept back is for the value at the tip. */}
            <div className="h-3 rounded-r bg-primary" style={{ width: `calc((100% - 4.5rem) * ${row.movies / peak})`, minWidth: 2 }} />
            <span className="tabular-nums">{row.movies.toLocaleString()}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** One whole split into parts, with every part's name, count and share in the legend. */
function StreamHealth(props: { readonly streams: LibraryStats['streams'] }) {
  const parts = STREAM_PARTS.map((part) => ({ ...part, count: props.streams[part.status] })).filter((part) => part.count > 0);
  const total = parts.reduce((sum, part) => sum + part.count, 0);
  if (total === 0) {
    return <p className="text-sm text-muted-foreground">No streams indexed yet.</p>;
  }
  const share = (count: number) => {
    const percent = (count / total) * 100;
    return percent < 1 ? '<1%' : `${Math.round(percent)}%`;
  };
  return (
    <div className="space-y-3">
      <div className="flex h-2 gap-0.5 overflow-hidden rounded-full" role="img" aria-label={parts.map((part) => `${part.label} ${share(part.count)}`).join(', ')}>
        {parts.map((part) => (
          <div key={part.status} className={cn('h-full', part.className)} style={{ width: `${(part.count / total) * 100}%` }} />
        ))}
      </div>
      <ul className="space-y-1.5 text-sm">
        {parts.map((part) => (
          <li key={part.status} className="flex items-center gap-2">
            <span className={cn('size-2.5 shrink-0 rounded-sm', part.className)} />
            <span className="text-muted-foreground">{part.label}</span>
            <span className="ml-auto tabular-nums">{part.count.toLocaleString()}</span>
            <span className="w-10 text-right text-xs text-muted-foreground tabular-nums">{share(part.count)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="space-y-8">
      <Skeleton className="h-16 w-56" />
      <div className={TILE_GRID}>
        {Array.from({ length: 8 }, (_unused, index) => (
          <Skeleton key={index} className="h-20 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

/** How big the library is, what it is made of, and how it grew. */
export function StatsPage() {
  const query = useStats();
  if (query.isError) {
    // The web app ships apart from the service, so a 404 here means one from before this page.
    return query.error instanceof NotFoundError ? (
      <div className="space-y-6">
        <PageHeader title="Stats" />
        <p className="text-sm text-muted-foreground">The zyx service you are connected to is too old to report these. Update it to see them.</p>
      </div>
    ) : (
      <ErrorState error={query.error} onRetry={() => void query.refetch()} />
    );
  }
  const stats = query.data?.stats;
  const today = localDate(new Date());
  const hours = Math.round((stats?.runtimeS ?? 0) / 3600);
  const addedThisWeek = stats === undefined ? 0 : addedInLast(stats.addedByDay, 7, today);

  return (
    <div className="space-y-8">
      <PageHeader title="Stats" />
      {stats === undefined ? (
        <StatsSkeleton />
      ) : (
        <>
          <section className="space-y-1">
            <p className="text-5xl font-semibold tracking-tight sm:text-6xl">{stats.movies.toLocaleString()}</p>
            <p className="text-sm text-muted-foreground">
              {[
                stats.movies === 1 ? 'movie in the database' : 'movies in the database',
                stats.browsableMovies === stats.movies ? undefined : `${stats.browsableMovies.toLocaleString()} shown when browsing`,
                `${addedThisWeek.toLocaleString()} added in the last 7 days`,
              ]
                .filter((part) => part !== undefined)
                .join(' · ')}
            </p>
          </section>
          <div className={TILE_GRID}>
            <Tile label="Variants" value={stats.variants.toLocaleString()} />
            <Tile label="People" value={stats.people.toLocaleString()} />
            {TERM_KINDS.filter((kind) => stats.terms[kind] > 0).map((kind) => (
              <Tile key={kind} label={PLURALS[kind]} value={stats.terms[kind].toLocaleString()} />
            ))}
            <Tile label="Total runtime" value={`${hours.toLocaleString()} h`} hint={`${(hours / 24 / 365.25).toFixed(1)} years`} />
            <Tile label="Cached files" value={formatBytes(stats.fileBytes)} hint={`${stats.files.toLocaleString()} files`} />
          </div>
          <div className="grid gap-8 lg:grid-cols-2">
            <Section title="Editions" note="Movies with each edition; one movie can have several.">
              <EditionBars editions={stats.editions} />
            </Section>
            <Section title="Streams" note="Episodes, by their last stream check.">
              <StreamHealth streams={stats.streams} />
            </Section>
            <Section title="Released" note="Movies by release year.">
              <ColumnChart title="Year" unit="movies" points={yearPoints(stats.releasedByYear)} />
            </Section>
            <Section title="Added" note="Movies first indexed each day, over the last 90 days.">
              <ColumnChart title="Day" unit="movies" points={dayPoints(stats.addedByDay, today)} />
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
